import React, { ReactElement, useCallback, useState, useEffect, useMemo } from "react";
import {
  Button,
  makeStyles,
  TextField,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@material-ui/core";
import theme from "../../../themes";
import { PhoneNumberFormatCustom } from "../../common/utilities/PhoneNumberFormat";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  BannerContentTypeEnum,
  ChamberMemberInput,
  LoginInfoEmailTypes,
  useUpdateChamberMemberMutation,
  useGetChamberMemberQuery,
  useDeleteChamberMemberMutation,
  useSetNewTemporaryChamberMemberPasswordMutation,
  useGetAdminConsoleLoginEmailForChamberMemberQuery,
  useSendLoginInfoEmailToChamberMemberMutation,
  useChamberMemberChangeAdminConsoleLoginEmailMutation,
} from "../../../graphql/types";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useFormik } from "formik";
import * as yup from "yup";
import { useLocation } from "react-router-dom";
import useRedirect from "../../common/Hooks/useRedirect";
import Categories from "../../common/utilities/Categories";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import _without from "lodash/without";
import omitDeep from "omit-deep-lodash";
import LogoPicker from "../../common/utilities/LogoPicker";
import ProvincePicker from "../../common/utilities/ProvincePicker";
import { getInitialStateOfCustomContacts } from "../../common/utilities/MultiContactForm";
import ChamberMemberIsSuspendedRadioGroup from "../../common/ChamberMemberIsSuspendedRadioGroup";
import DeleteLogoModal from "./DeleteLogoModal";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import ResetPasswordIcon from "../../../images/resetfinal.png";
import ResetDashboardPasswordDialog from "../../common/ResetDashboardPasswordDialog";
import SendLoginInformationModal, { SampleLoginInfoEmailModal } from "../../common/SendLoginInformationModal";
import ResetPasswordButtonIcon from "../../../images/reset-password-black.png";
import UploadRoundIcon from "../../../images/upload-rounded.png";
import TrashIcon from "../../../images/trash.png";
import UploadIcon from "../../../images/upload-rounded.png";
import TickCircleIcon from "../../../images/tick-circle.png";
import PlayVideoIcon from "../../../images/play-video-icon.svg";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CheckIcon from "@material-ui/icons/Check";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import IconButton from "@material-ui/core/IconButton";
import IdaChatbotIcon from "../../../images/ida-chatbot-icon.png";
import AdaChatAIIcon from "../../../images/adachatai.png";
import AIRewriteIcon from "../../../images/airewrite.png";
import IDAOverwriteDialog from "../../common/IDAOverwriteDialog";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import SuccessDialog from "../../common/SuccessDialog";
import BannerUploadModal from "./BannerUploadModal";
import BannerPreviewModal from "./BannerPreviewModal";
import DeleteBannerModal from "./DeleteBannerModal";
import IDAGenerateModal from "../../common/IDAGenerateModal";
import { CSVLink } from "react-csv";
import {
  CHARACTER_LIMIT_DESCRIPTION,
  addressValidationSchema,
} from "./memberFormUtils";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import FeaturedMemberBannerGuidance from "../../common/FeaturedMemberBannerGuidance";
const useStyles = makeStyles((themes: any) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    minHeight: "100vh",
    paddingTop: "10px",
    overflowX: "hidden" as const,
  },
  background: {},

  title: {
    fontSize: "32px",
    color: "white",
    fontWeight: "bold",
    marginBottom: "10px",
    marginTop: "5px",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "15px 40px",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 16px",
    },
  },
  pageHeaderButtonRow: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > div": { width: "100%" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  headerIcons: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
  },
  headerIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  contentContainer: {
    height: "100vh",
    backgroundColor: "#F6F6F6",
  },
  sectionHeader: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "30px",
    padding: "12px 32px",
    display: "inline-block",
    position: "relative",
    top: "-24px",
    left: "0",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 18px",
      top: "-20px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  sectionHeaderInRow: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "25px",
    padding: "10px 30px",
    display: "inline-block",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 16px",
    },
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    margin: 0,
    textAlign: "center",
    lineHeight: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "18px",
    },
  },
  sectionHelperText: {
    color: "#000000",
    fontSize: "16px",
    lineHeight: "18px",
    fontWeight: 400,
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
      marginBottom: "14px",
    },
  },
  formSection: {
    maxWidth: "1096px",
    margin: "50px auto 50px auto",
    padding: "0px 30px 30px 30px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      margin: "40px 12px 24px 12px",
      padding: "0 16px 20px 16px",
      maxWidth: "calc(100% - 24px)",
    },
  },
  formSectionWithButtons: {
    maxWidth: "1096px",
    margin: "50px auto 50px auto",
    padding: "0px 30px 30px 30px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      margin: "40px 12px 24px 12px",
      padding: "0 16px 20px 16px",
      maxWidth: "calc(100% - 24px)",
    },
  },
  sectionHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "-25px",
    marginBottom: "0px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  companyInfoGrid: {
    display: "grid",
    gridTemplateColumns: "409fr 537fr",
    gap: "20px 20px",
    gridTemplateAreas: `
      "companyName description"
      "companyWebsite description"
      "contactName phone"
      "contactEmail googleReview"
      "logo categories"
    `,
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gridTemplateAreas: `
        "companyName"
        "description"
        "companyWebsite"
        "contactName"
        "phone"
        "contactEmail"
        "googleReview"
        "logo"
        "categories"
      `,
    },
  },
  formRowSingle: {
    display: "grid",
    gridTemplateColumns: "1fr",
    marginBottom: "20px",
  },
  orangeHelperText: {
    color: theme.palette.primary.main,
    fontSize: "12px",
    marginBottom: "15px",
    lineHeight: "1.5",
  },
  checkboxLabel: {
    marginBottom: "15px",
    marginLeft: "0",
  },
  buttonContainer: {
    display: "flex",
    paddingBottom: "9px",
    paddingLeft: "24px",
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    [theme.breakpoints.up("lg")]: {
      paddingTop: "15px",
    },
    "@media (max-width: 768px)": {
      minHeight: "auto",
    },
  },
  companyInput: {
    paddingBottom: "10px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderRadius: "8px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.text.primary,
      },
    },
    "& label.Mui-focused": {
      color: "#000000",
    },
    "& label.Mui": {
      color: "black",
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
      "&::placeholder": {
        color: "#333333",
        opacity: 1,
      },
    },
  },
  sectionInput: {
    paddingBottom: "10px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderRadius: "8px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.text.primary,
      },
    },
    "& label.Mui-focused": {
      color: "#000000",
    },
    "& label.Mui": {
      color: "black",
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
    },
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    fontSize: "18px",
    color: "#000000",
    fontWeight: 600,
    marginBottom: "8px",
  },
  requiredAsterisk: {
    color: "red",
  },
  logoHelperText: {
    fontSize: "16px",
    color: theme.palette.text.primary,
    marginBottom: "12px",
    lineHeight: "18px",
  },
  descriptionHelperText: {
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    color: "#24272C",
    lineHeight: "22px",
    marginBottom: "4px",
  },
  categoryHelperText: {
    fontSize: "16px",
    color: theme.palette.text.primary,
    marginBottom: "12px",
    lineHeight: "18px",
  },
  bannerHelperText: {
    color: "#000000",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    marginBottom: "15px",
  },
  content: {
    background: "#FFFFFF",
    flexGrow: 1,
    borderRadius: "16px",
    margin: "20px",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
    flex: "1",
    marginTop: "33px",
    paddingTop: "30px",
  },
  invalidError: {
    textAlign: "center",
    color: "red",
  },
  actionButtonsContainer: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    maxWidth: "1096px",
    margin: "0 auto 16px auto",
    paddingLeft: "30px",
  },
  resetPasswordButton: {
    backgroundColor: "#DEE2E6",
    color: "#000000",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: "17px",
    borderRadius: "10px",
    padding: "5px",
    textTransform: "none" as const,
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    width: "145px",
    height: "39px",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#CED4DA",
    },
  },
  deleteButton: {
    backgroundColor: (theme.palette as any).delete.main,
    color: "#FFFFFF",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: "17px",
    borderRadius: "10px",
    padding: "0px",
    textTransform: "none" as const,
    border: `1px solid ${(theme.palette as any).delete.main}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    width: "145px",
    height: "39px",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#D41820",
      border: "1px solid #D41820",
    },
  },
  lockIcon: {
    width: "16px",
    height: "16px",
    marginRight: "5px",
  },
  deleteIcon: {
    width: "16px",
    height: "16px",
    filter: "brightness(0) invert(1)",
    marginRight: "5px",
  },
  updateButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontSize: "24px",
    fontWeight: "bold",
    borderRadius: "12px",
    padding: "15px 96px",
    textTransform: "none",
    minWidth: "350px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
      padding: "14px 32px",
      minWidth: 0,
      width: "100%",
      maxWidth: "320px",
    },
  },
  // IDA Helper & Generate styles
  idaHelperContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    padding: "2px 5px",
    marginTop: "8px",
    backgroundColor: "#FFFFFF",
  },
  idaIcon: {
    width: "33px",
    height: "26px",
    objectFit: "contain" as const,
  },
  idaHelperText: {
    fontWeight: 400,
    fontSize: "12px",
    color: theme.palette.primary.main,
    lineHeight: "15px",
    "& b": {
      fontWeight: 700,
    },
  },
  idaGenerateBtn: {
    backgroundColor: "#4CAF50",
    border: "1px solid #4CAF50",
    borderRadius: "10px",
    padding: "1px 13px",
    height: "17px",
    minWidth: "71px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#388E3C",
    },
  },
  idaGenerateBtnText: {
    fontWeight: 400,
    fontSize: "10px",
    color: "#FFFFFF",
    lineHeight: "15px",
  },
  idaDialogPaper: {
    borderRadius: "20px",
    maxWidth: "520px",
    width: "520px",
    overflow: "hidden",
    padding: "0",
    backgroundColor: "#FFFFFF",
    position: "relative" as const,
  },
  idaDialogCloseButton: {
    position: "absolute" as const,
    right: "10px",
    top: "10px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "28px",
    height: "28px",
    padding: 0,
    zIndex: 1,
    "&:hover": { backgroundColor: "#D41920" },
  },
  idaDialogMascotRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "4px",
    marginBottom: "8px",
    paddingLeft: "20px",
    paddingTop: "16px",
  },
  idaDialogSpeechBubble: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #ccc",
    borderRadius: "12px",
    padding: "6px 12px",
    fontSize: "12px",
    color: "#333",
  },
  idaDialogMascotImg: {
    width: "80px",
    height: "80px",
    objectFit: "contain" as const,
  },
  idaDialogTitle: {
    fontWeight: 700,
    fontSize: "18px",
    color: theme.palette.primary.main,
    textAlign: "center" as const,
    lineHeight: "1.4",
    margin: "0 24px 8px 24px",
  },
  idaDialogSubtitle: {
    fontSize: "13px",
    color: "#333",
    textAlign: "center" as const,
    lineHeight: "1.5",
    margin: "0 24px 12px 24px",
  },
  idaDialogTextarea: {
    margin: "0 24px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "12px",
    fontSize: "13px",
    lineHeight: "1.6",
    resize: "vertical" as const,
    minHeight: "100px",
    outline: "none",
    "&:focus": { borderColor: theme.palette.primary.main },
  },
  idaDialogGenerateBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "uppercase" as const,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "16px auto 20px auto",
    "&:hover": { backgroundColor: "#D4A353" },
  },
  overwriteDialogPaper: {
    borderRadius: "20px",
    maxWidth: "600px",
    width: "600px",
    position: "relative" as const,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  overwriteCloseButton: {
    position: "absolute" as const,
    right: "12px",
    top: "12px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "white",
    width: "30px",
    height: "30px",
    padding: 0,
    zIndex: 1,
    "&:hover": { backgroundColor: "#D41920" },
  },
  overwriteIconCircle: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
  },
  overwriteTitle: {
    fontWeight: 700,
    fontSize: "20px",
    textAlign: "center" as const,
    color: "#000",
    marginBottom: "8px",
  },
  overwriteDesc: {
    fontSize: "14px",
    textAlign: "center" as const,
    color: "#333",
    lineHeight: "1.6",
    marginBottom: "16px",
    padding: "0 20px",
  },
  overwritePreviewBox: {
    border: `1px solid ${theme.palette.success.main}`,
    borderRadius: "6px",
    padding: "12px 6px",
    margin: "0 50px",
    maxHeight: "200px",
    overflow: "auto",
  },
  overwritePreviewLabel: {
    fontSize: "13px",
    fontWeight: 700,
    color: theme.palette.success.main,
    marginBottom: "8px",
  },
  overwritePreviewText: {
    fontSize: "13px",
    color: theme.palette.text.primary,
    lineHeight: "1.6",
  },
  overwriteButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    margin: "20px 0 24px 0",
  },
  overwriteRegenBtn: {
    backgroundColor: theme.palette.success.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 24px",
    textTransform: "uppercase" as const,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": { backgroundColor: "#009A1B" },
  },
  overwriteConfirmBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "uppercase" as const,
    "&:hover": { backgroundColor: "#C99028" },
  },
  idaSuccessDialogPaper: {
    borderRadius: "20px",
    maxWidth: "420px",
    width: "420px",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
    position: "relative",
  },
  idaSuccessIconCircle: {
    width: "90px",
    height: "90px",
    borderRadius: "50%",
    backgroundColor: "#22C55E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
  },
  idaSuccessTitle: {
    fontWeight: 700,
    fontSize: "20px",
    textAlign: "center" as const,
    color: "#000",
    lineHeight: "1.5",
  },
  bannerUploadContainer: {
    backgroundColor: "#FEF5E7",
    borderRadius: "12px",
    padding: "40px 20px",
    textAlign: "center",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "150px",
    border: "none",
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: "#FDE9C9",
    },
  },
  uploadIcon: {
    width: "48px",
    height: "48px",
    marginBottom: "12px",
  },
  uploadText: {
    color: theme.palette.primary.main,
    fontSize: "20px",
    fontWeight: 600,
    margin: 0,
  },
  bannerPreview: {
    maxWidth: "100%",
    maxHeight: "200px",
    marginTop: "10px",
    borderRadius: "8px",
  },
  bannersGrid: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "20px",
    marginTop: "20px",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px 12px",
      alignItems: "stretch",
      justifyContent: "stretch",
      width: "100%",
    },
  },
  dialogPaper: {
    borderRadius: "20px",
    padding: "20px",
    maxWidth: "500px",
    position: "relative",
    overflow: "visible",
  },
  dialogCloseButton: {
    position: "absolute",
    right: "10px",
    top: "10px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "#FFFFFF",
    width: "36px",
    height: "36px",
    padding: 0,
    borderRadius: "100px",
    "&:hover": {
      backgroundColor: "#DC2626",
    },
  },
  dialogIconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
    marginTop: "20px",
  },
  dialogIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogIconReset: {
  },
  dialogIconDelete: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: (theme.palette as any).delete.main,
  },
  dialogIconSuccess: {
    border: "3px solid #22C55E",
    backgroundColor: "#DCFCE7",
  },
  dialogTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "16px",
    color: "#000",
  },
  dialogDescription: {
    fontSize: "14px",
    textAlign: "left",
    color: "#666",
    marginBottom: "12px",
    lineHeight: "1.6",
  },
  dialogConfirmText: {
    fontSize: "16px",
    fontWeight: "bold",
    textAlign: "center",
    marginTop: "24px",
    marginBottom: "24px",
    color: "#000",
  },
  dialogButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "24px",
  },
  dialogButtonYes: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "12px 48px",
    textTransform: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  dialogButtonNo: {
    backgroundColor: "#333",
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "12px 48px",
    textTransform: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  dialogButtonDelete: {
    backgroundColor: "#EF4444",
    color: "white",
    fontWeight: "bold",
    borderRadius: "12px",
    padding: "14px 56px",
    textTransform: "none" as const,
    fontSize: "18px",
    flex: 1,
    "&:hover": {
      backgroundColor: "#DC2626",
    },
  },
  dialogButtonCancel: {
    backgroundColor: "#333",
    color: "white",
    fontWeight: "bold",
    borderRadius: "12px",
    padding: "14px 56px",
    textTransform: "none" as const,
    fontSize: "18px",
    flex: 1,
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  successDialogPaper: {
    borderRadius: "16px",
    padding: "20px",
    maxWidth: "600px",
    position: "relative",
  },
  resetSuccessDialogPaper: {
    borderRadius: "20px",
    padding: "0",
    maxWidth: "min(880px, 95vw)",
    width: "100%",
    position: "relative",
    overflow: "auto",
  },
  resetSuccessSection: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "16px",
    padding: "24px",
    display: "flex",
    flexDirection: "column" as const,
  },
  resetSuccessSectionDark: {
    border: "1px solid #000000",
    backgroundColor: "#FFFFFF",
  },
  resetSuccessSectionTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "22px",
    lineHeight: "150%",
    marginBottom: "12px",
    textAlign: "left" as const,
  },
  resetSuccessSectionTitleBold: {
    fontSize: "22px",
    fontWeight: 700,
  },
  resetSuccessButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    borderRadius: "12px",
    padding: "10px 48px",
    textTransform: "uppercase" as const,
    fontSize: "14px",
    lineHeight: "150%",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      backgroundColor: "#C99028",
    },
  },
  resetSendEmailButton: {
    backgroundColor: "#FFFFFF",
    color: "#000000",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    borderRadius: "12px",
    padding: "10px 24px",
    textTransform: "uppercase" as const,
    fontSize: "14px",
    lineHeight: "150%",
    border: "1px solid #000000",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    whiteSpace: "nowrap" as const,
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
  resetSuccessButtonOutline: {
    backgroundColor: "#000000",
    color: "#FFFFFF",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    borderRadius: "12px",
    padding: "10px 24px",
    textTransform: "uppercase" as const,
    fontSize: "14px",
    lineHeight: "150%",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    whiteSpace: "nowrap" as const,
    "&:hover": {
      backgroundColor: "#333333",
    },
  },
  // Update success popup
  updateSuccessDialogPaper: {
    borderRadius: "20px",
    maxWidth: "500px",
    width: "500px",
    position: "relative",
    overflow: "visible",
    backgroundColor: "#FFFFFF",
  },
  updateSuccessIconContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
    marginBottom: "16px",
  },
  updateSuccessIcon: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#22C55E",
  },
  updateSuccessTitle: {
    fontSize: "20px",
    fontWeight: 700,
    textAlign: "center" as const,
    color: "#000000",
    marginBottom: "30px",
  },
  // Email already in use / error popup
  emailInUseDialogPaper: {
    borderRadius: "20px",
    maxWidth: "576px",
    width: "576px",
    position: "relative",
    overflow: "visible",
  },
  emailInUseCloseButton: {
    position: "absolute" as const,
    right: "15px",
    top: "15px",
    backgroundColor: "#FFFFFF",
    color: (theme.palette as any).delete.main,
    width: "30px",
    height: "30px",
    padding: 0,
    borderRadius: "100px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  emailInUseIconContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "15px",
    marginBottom: "0px",
  },
  emailInUseIcon: {
    width: "112px",
    height: "107px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: (theme.palette as any).delete.main,
  },
  emailInUseTitle: {
    fontSize: "25px",
    fontWeight: 700,
    textAlign: "center" as const,
    color: "#000000",
    margin: "0",
    lineHeight: "200%",
  },
  emailInUseDescription: {
    fontSize: "14px",
    lineHeight: "200%",
    textAlign: "center" as const,
    color: "#000000",
    marginBottom: "0px",
  },
  emailInUseButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "74px",
    marginTop: "20px",
    marginBottom: "10px",
  },
  emailInUseButtonAlt: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "16px",
    textTransform: "none" as const,
    borderRadius: "12px",
    padding: "15px 96px",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    whiteSpace: "nowrap" as const,
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  emailInUseButtonBack: {
    backgroundColor: theme.palette.text.primary,
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "16px",
    textTransform: "none" as const,
    borderRadius: "12px",
    padding: "15px 96px",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    whiteSpace: "nowrap" as const,
    "&:hover": {
      backgroundColor: "#333333",
    },
  },
  previewButton: {
    backgroundColor: "#333",
    color: "white",
    fontSize: "16px",
    fontWeight: "bold",
    borderRadius: "25px",
    padding: "12px 40px",
    textTransform: "none",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  previewBannersButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000000",
    color: "white",
    borderRadius: "12px",
    padding: "15px 96px",
    fontSize: "20px",
    fontWeight: 700,
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "#333333",
    },
    "&.Mui-disabled": {
      backgroundColor: "#888888",
      color: "#FFFFFF",
      opacity: 1,
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: "14px 24px",
      fontSize: "16px",
    },
  },
  bannerCard: {
    backgroundColor: "#FFFFFF",
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    width: "209px",
    height: "175px",
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "175px",
    },
  },
  bannerCardEmpty: {
    backgroundColor: "#FFFFFF",
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    width: "182px",
    height: "114px",
    boxSizing: "border-box" as const,
    cursor: "pointer",
    padding: "22px 12px",
    gap: "10px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "90px",
      padding: "12px 8px",
      gap: "8px",
    },
  },
  bannerSlotDisabled: {
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    padding: "22px 12px",
    gap: "10px",
    width: "182px",
    height: "114px",
    backgroundColor: "#9E9E9E",
    borderRadius: "10px",
    boxSizing: "border-box" as const,
    cursor: "not-allowed",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "90px",
      padding: "12px 8px",
      gap: "8px",
    },
  },
  bannerSlotDisabledIcon: {
    width: "36px",
    height: "36px",
    filter: "brightness(0) invert(1)",
    [theme.breakpoints.down("sm")]: {
      width: "28px",
      height: "28px",
    },
  },
  bannerSlotDisabledText: {
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "24px",
    textAlign: "center" as const,
    color: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      lineHeight: "1.3",
    },
  },
  sectionHeaderBlack: {
    backgroundColor: "#000000",
    borderRadius: "30px",
    padding: "12px 32px",
    display: "inline-block",
    position: "relative" as const,
    top: "-24px",
    left: "0",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 18px",
      top: "-20px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  sectionHeaderGreen: {
    backgroundColor: theme.palette.success.main,
    borderRadius: "30px",
    padding: "12px 32px",
    display: "inline-block",
    position: "relative" as const,
    top: "-24px",
    left: "0",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 18px",
      top: "-20px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  formSectionDashed: {
    maxWidth: "1096px",
    margin: "50px auto 50px auto",
    padding: "0px 30px 30px 30px",
    border: "1px dashed #9E9E9E",
    borderRadius: "16px",
    position: "relative" as const,
    [theme.breakpoints.down("sm")]: {
      margin: "40px 12px 24px 12px",
      padding: "0 16px 20px 16px",
      maxWidth: "calc(100% - 24px)",
    },
  },
  bannerImageContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative" as const,
    padding: "12px",
    flex: 1,
  },
  bannerImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    borderRadius: "4px",
  },
  videoPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "4px",
    color: theme.palette.primary.main,
    gap: "4px",
  },
  bannerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 12px",
    backgroundColor: "transparent",
  },
  editDeleteButtons: {
    display: "flex",
    gap: "8px",
  },
  editButton: {
    backgroundColor: "#E3E3E3",
    borderRadius: "5px",
    width: "36px",
    height: "33px",
    minWidth: "36px",
    padding: "4px",
    "&:hover": {
      backgroundColor: "#D0D0D0",
    },
  },
  trashButton: {
    backgroundColor: (theme.palette as any).delete.main,
    borderRadius: "5px",
    width: "36px",
    height: "33px",
    minWidth: "36px",
    padding: "4px",
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  arrowButtons: {
    display: "flex",
    gap: "4px",
  },
  arrowButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: "4px",
    borderRadius: "5px",
    minWidth: "29px",
    width: "29px",
    height: "26px",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
    "&.Mui-disabled": {
      backgroundColor: "#D9D9D9",
      color: "#666",
    },
  },
  // Banner section text styles
  bannerInfoText: {
    fontSize: "16px",
    lineHeight: "24px",
    color: "#000000",
    textAlign: "center" as const,
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  bannerInfoTextLarge: {
    fontSize: "20px",
    lineHeight: "24px",
    color: theme.palette.text.secondary,
    textAlign: "center" as const,
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "15px",
      lineHeight: "20px",
    },
  },
  bannerRecommendationText: {
    fontSize: "16px",
    lineHeight: "24px",
    color: "#000000",
    textAlign: "center" as const,
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  boldText: {
    fontWeight: 700,
  },
  goldLink: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    textDecoration: "underline",
    cursor: "pointer",
  },
  bannerContainer: {
    display: "flex",
    width: "209px",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "16px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      gap: "8px",
      minWidth: 0,
    },
  },
  bannerLabel: {
    textAlign: "center" as const,
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: "20px",
    lineHeight: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "15px",
    },
  },
  uploadBannerText: {
    color: theme.palette.primary.main,
    fontSize: "20px",
    fontWeight: 600,
    margin: 0,
    lineHeight: "24px",
    textAlign: "center" as const,
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      lineHeight: "1.3",
    },
  },
  iconSize36: {
    width: "36px",
    height: "36px",
    [theme.breakpoints.down("sm")]: {
      width: "28px",
      height: "28px",
    },
  },
  iconSize20: {
    width: "20px",
    height: "20px",
  },
  iconInverted: {
    filter: "brightness(0) invert(1)",
  },
  videoWatchText: {
    fontSize: "14px",
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  arrowIconSmall: {
    fontSize: "12px",
  },
  arrowIconLeft: {
    fontSize: "12px",
    marginLeft: "4px",
  },
  centerFlexContainer: {
    display: "flex",
    justifyContent: "center",
  },
}));

const EditChamberMember = (): ReactElement => {
  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const enumStrings = useEnumStrings();
  const location = useLocation();

  const chamberMemberId = useMemo(() => {
    const stateChamberMemberId = (
      (location.state || { chamberMemberId: undefined }) as {
        chamberMemberId: string | undefined;
      }
    ).chamberMemberId;

    if (stateChamberMemberId) {
      return stateChamberMemberId;
    }

    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("chamberMemberId") || undefined;
  }, [location.search, location.state]);

  const scrollToSection = useMemo(() => {
    return (
      (location.state || { scrollToSection: undefined }) as {
        scrollToSection: string | undefined;
      }
    ).scrollToSection;
  }, [location.state]);

  const adminContactRef = React.useRef<HTMLDivElement>(null);
  const savedValuesRef = React.useRef<string>("");

  // Track whether user has directly interacted with admin fields (stops auto-sync for that field)
  const adminFieldsTouched = React.useRef({
    adminConsoleContactName: false,
    cpaAdminCompanyAdminContactTitle: false,
    adminConsoleLoginEmail: false,
    cpaAdminCompanyAdminContactNumber: false,
    cpaAdminCompanyAdminContactNumberExtension: false,
  });

  // Track whether primary and admin fields initially had the same value (linked state)
  const fieldsInitiallyLinked = React.useRef({
    adminConsoleContactName: false,
    cpaAdminCompanyAdminContactTitle: false,
    adminConsoleLoginEmail: false,
    cpaAdminCompanyAdminContactNumber: false,
    cpaAdminCompanyAdminContactNumberExtension: false,
  });

  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [customError, setCustomError] = useState("");
  const [invalidError, setInvalidError] = useState(false);
  const [dialogState, setDialogState] = useState<{ open: boolean; message: string; variant: "success" | "error" }>({ open: false, message: "", variant: "success" });
  const [updateChamberMember] = useUpdateChamberMemberMutation();
  const [deleteChamberMember] = useDeleteChamberMemberMutation();
  const [resetPassword] = useSetNewTemporaryChamberMemberPasswordMutation();
  const [sendLoginInfoEmail] = useSendLoginInfoEmailToChamberMemberMutation();
  const [changeAdminEmail] = useChamberMemberChangeAdminConsoleLoginEmailMutation();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const [categoryDisable, setCategoryDisable] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordSuccessDialogOpen, setResetPasswordSuccessDialogOpen] = useState(false);
  const [resetPasswordPreviewEmailOpen, setResetPasswordPreviewEmailOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingCredentials, setDownloadingCredentials] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [updateSuccessDialogOpen, setUpdateSuccessDialogOpen] = useState(false);
  const [sendCredentialsDialogOpen, setSendCredentialsDialogOpen] = useState(false);
  const [emailInUseDialogOpen, setEmailInUseDialogOpen] = useState(false);
  const [alternativeEmail, setAlternativeEmail] = useState("");

  // IDA Generate states
  const [idaDialogOpen, setIdaDialogOpen] = useState(false);
  const [idaKeyDetails, setIdaKeyDetails] = useState("");
  const [idaGenerating, setIdaGenerating] = useState(false);
  const [idaGeneratedText, setIdaGeneratedText] = useState("");
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [idaSuccessDialogOpen, setIdaSuccessDialogOpen] = useState(false);
  const [idaGeneratedDescription, setIdaGeneratedDescription] = useState(false);
  const [deleteLogoConfirmationOpen, setDeleteLogoConfirmationOpen] = useState(false);
  const [isFeaturedMember, setIsFeaturedMember] = useState(false);

  // Preview Modal State
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewBannersList, setPreviewBannersList] = useState<
    Array<{
      image: string | null;
      videoUrl: string | null;
      redirectUrl: string | null;
    }>
  >([]);

  const [newPassword, setNewPassword] = useState("");

  // Banner Modal State
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(-1);
  const [bannersData, setBannersData] = useState<
    Array<{
      image: string | null;
      videoUrl: string | null;
      redirectUrl: string | null;
    }>
  >([
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
  ]);

  const handleOpenBannerModal = (index: number) => {
    setCurrentBannerIndex(index);
    setBannerModalOpen(true);
  };

  const handleSaveBanner = (data: {
    image: string | null;
    videoUrl: string | null;
    redirectUrl: string | null;
  }) => {
    const newBannersData = [...bannersData];
    newBannersData[currentBannerIndex - 1] = data;
    setBannersData(newBannersData);
    setBannerModalOpen(false);
    // TODO: You might want to update formik here or call a mutation
    // formik.setFieldValue(`banners[${currentBannerIndex-1}]`, data);
  };



  const handleMoveBanner = (index: number, direction: 'left' | 'right') => {
    const newBannersData = [...bannersData];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newBannersData.length) {
      const temp = newBannersData[index];
      newBannersData[index] = newBannersData[targetIndex];
      newBannersData[targetIndex] = temp;
      setBannersData(newBannersData);
    }
  };

  const handleDeleteBanner = (index: number) => {
    setPendingDeleteIndex(index);
    setDeleteModalOpen(true);
  };

  const confirmDeleteBanner = () => {
    const newBannersData = [...bannersData];
    newBannersData[pendingDeleteIndex] = { image: null, videoUrl: null, redirectUrl: null };
    setBannersData(newBannersData);
    setDeleteModalOpen(false);
    setPendingDeleteIndex(-1);
  };

  const handlePreviewBanners = () => {
    setPreviewBannersList(bannersData);
    setPreviewModalOpen(true);
  };

  const { data: memberData, loading: memberLoading } = useGetChamberMemberQuery({
    variables: { chamberMemberId: chamberMemberId || "" },
    skip: !chamberMemberId,
  });

  const { data: adminConsoleLoginEmailData } = useGetAdminConsoleLoginEmailForChamberMemberQuery({
    variables: { chamberMemberId: chamberMemberId || "" },
    skip: !chamberMemberId,
    fetchPolicy: "network-only",
  });

  const initialValues: ChamberMemberInput = {
    id: "",
    chamberId: "",
    categoryId: [],
    name: "",
    description: "",
    website: "",
    facebook: "",
    instagram: "",
    threads: "",
    linkedin: "",
    twitter: "",
    tiktok: "",
    youtube: "",
    googleReviewLink: "",
    address: {
      city: "",
      postalCode: "",
      province: "",
      streetAddress: "",
    },
    phoneNumber: "",
    phoneNumberExtension: "",
    contactName: "",
    email: "",
    logo: "",
    showInApp: true,
    hideAddressFromDirectory: false,
    adminConsoleLoginEmail: "",
    primaryContactName: "",
    primaryContactTitle: "",
    primaryContactEmail: "",
    primaryContactPhoneNumber: "",
    primaryContactPhoneNumberExtension: "",
    adminConsoleContactName: "",
    cpaAdminCompanyAdminContactTitle: "",
    cpaAdminCompanyAdminContactNumber: "",
    cpaAdminCompanyAdminContactNumberExtension: "",
    cpaEmployeeAdoptionContactName: "",
    cpaEmployeeAdoptionContactEmail: "",
    cpaEmployeeAdoptionContactTitle: "",
    cpaEmployeeAdoptionContactNumber: "",
    cpaEmployeeAdoptionContactNumberExtension: "",
    customContacts: getInitialStateOfCustomContacts(null),
    isSuspended: false,
  };

  const redirectToManageChamber = useRedirect(
    RedirectUri.manageAMemberChamberAdmin
  );

  const validationSchema = yup.object({
    name: yup
      .string()
      .required(enumStrings.validationSchema.companyNameRequired),
    description: yup.string(),
    primaryContactName: yup
      .string()
      .required(enumStrings.validationSchema.primaryContactNameRequired || "Primary Contact Name is required"),
    primaryContactEmail: yup
      .string()
      .email(enumStrings.validationSchema.notValidEmail)
      .required(enumStrings.validationSchema.adminConsoleLoginEmailRequired || "Primary Contact Email is required"),
    primaryContactPhoneNumber: yup
      .string(),
    primaryContactTitle: yup
      .string(),
    adminConsoleContactName: yup
      .string()
      .required(enumStrings.validationSchema.dashboardContactNameRequired),
    adminConsoleLoginEmail: yup
      .string()
      .email(enumStrings.validationSchema.notValidEmail)
      .required(enumStrings.validationSchema.adminConsoleLoginEmailRequired || "Administrator Contact Email is required"),
    cpaAdminCompanyAdminContactNumber: yup
      .string(),
    phoneNumber: yup.string(),
    email: yup.string().email(enumStrings.validationSchema.notValidEmail),
    cpaEmployeeAdoptionContactEmail: yup.string().email(enumStrings.validationSchema.notValidEmail),
    cpaEmployeeAdoptionContactNumber: yup.string(),
    website: yup.string(),
    address: addressValidationSchema,
    categoryId: yup
      .array()
      .min(1, enumStrings.validationSchema.categoryRequired),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: any) => {
      setInvalidError(false);
      setCustomError(``);

      if (!chamberInfo || !chamberInfo.id) {
        setInvalidError(true);
        setCustomError(
          `${enumStrings.perkPartnerAdmin.unableToCreateMemberPleaseTryAgin}`
        );
        return;
      }

      setLoader(true);
      try {
        setButtonDisabled(true);
        const bannerArray = bannersData
          .map((b, index) => {
            if (b.image) {
              return {
                contentUrl: b.image,
                imageLink: b.redirectUrl || "",
                contentType: BannerContentTypeEnum.Image,
                position: index,
              };
            } else if (b.videoUrl) {
              return {
                contentUrl: b.videoUrl,
                imageLink: b.redirectUrl || "",
                contentType: BannerContentTypeEnum.Video,
                position: index,
              };
            }
            return null;
          })
          .filter((b): b is NonNullable<typeof b> => b !== null);

        const mutationInput = omitDeep({
          id: chamberMemberId || "",
          address: {
            city: input.address.city || "",
            postalCode: input.address.postalCode || "",
            province: input.address.province || "",
            streetAddress: input.address.streetAddress || "",
          },
          categoryId: input.categoryId,
          chamberId: chamberInfo.id,
          description: (input.description || "").trim(),
          contactName: (input.contactName || "").trim(),
          email: (input.email || "").trim().toLowerCase(),
          phoneNumber: input.phoneNumber,
          phoneNumberExtension: input.phoneNumberExtension || "",
          showInApp: input.showInApp,
          hideAddressFromDirectory: input.hideAddressFromDirectory ?? false,
          name: input.name.trim(),
          website: input.website,
          facebook: input.facebook,
          instagram: input.instagram,
          threads: input.threads,
          linkedin: input.linkedin,
          twitter: input.twitter,
          tiktok: input.tiktok,
          youtube: input.youtube,
          googleReviewLink: input.googleReviewLink,
          logo: input.logo,
          customContacts: input.customContacts,
          primaryContactName: (input.primaryContactName || "").trim(),
          primaryContactTitle: (input.primaryContactTitle || "").trim(),
          primaryContactEmail: (input.primaryContactEmail || "").trim().toLowerCase(),
          primaryContactPhoneNumber: input.primaryContactPhoneNumber || "",
          primaryContactPhoneNumberExtension: input.primaryContactPhoneNumberExtension || "",
          adminConsoleContactName: (input.adminConsoleContactName || "").trim(),
          cpaAdminCompanyAdminContactTitle: (input.cpaAdminCompanyAdminContactTitle || "").trim(),
          cpaAdminCompanyAdminContactNumber: input.cpaAdminCompanyAdminContactNumber || "",
          cpaAdminCompanyAdminContactNumberExtension: input.cpaAdminCompanyAdminContactNumberExtension || "",
          cpaEmployeeAdoptionContactName: (input.cpaEmployeeAdoptionContactName || "").trim(),
          cpaEmployeeAdoptionContactEmail: (input.cpaEmployeeAdoptionContactEmail || "").trim().toLowerCase(),
          cpaEmployeeAdoptionContactTitle: (input.cpaEmployeeAdoptionContactTitle || "").trim(),
          cpaEmployeeAdoptionContactNumber: input.cpaEmployeeAdoptionContactNumber || "",
          cpaEmployeeAdoptionContactNumberExtension: input.cpaEmployeeAdoptionContactNumberExtension || "",
          isSuspended: input.isSuspended,
          banner: bannerArray.length > 0 ? bannerArray : undefined,
        }, "__typename");

        // Check if admin login email has changed
        const currentAdminEmail = adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || "";
        const newAdminEmail = (input.adminConsoleLoginEmail || "").trim().toLowerCase();
        let adminEmailChanged = false;

        if (newAdminEmail && newAdminEmail !== currentAdminEmail.toLowerCase()) {
          const { data: emailChangeData } = await changeAdminEmail({
            variables: {
              chamberMemberId: chamberMemberId || "",
              currentAdminConsoleLoginEmail: currentAdminEmail,
              newAdminConsoleLoginEmail: newAdminEmail,
            },
            refetchQueries: ["getAdminConsoleLoginEmailForChamberMember"],
            awaitRefetchQueries: true,
          });

          if (emailChangeData?.chamberMemberChangeAdminConsoleLoginEmail?.adminConsoleLoginEmailChangedSuccessfully === false) {
            const failureReason = emailChangeData.chamberMemberChangeAdminConsoleLoginEmail.failureReason || "";

            if (failureReason.includes("UsernameExistsException")) {
              const suggestedEmail = failureReason.trim().split(" ")[1] || "";
              setAlternativeEmail(suggestedEmail);
              setEmailInUseDialogOpen(true);
            } else {
              setInvalidError(true);
              setCustomError(failureReason || enumStrings.toastMessages.failedToUpdateMember);
            }
            setLoader(false);
            setButtonDisabled(false);
            return;
          }
          adminEmailChanged = true;
        }

        await updateChamberMember({
          variables: {
            id: chamberMemberId || "",
            input: mutationInput,
          },
          refetchQueries: ["getChamberMember", "getAllChamberMembers"],
        });

        setLoader(false);
        setButtonDisabled(false);
        savedValuesRef.current = JSON.stringify(formik.values);

        if (adminEmailChanged) {
          // Show Send Login Credentials dialog when admin email changed
          setSendCredentialsDialogOpen(true);
        } else {
          setUpdateSuccessDialogOpen(true);
          // Auto-close success dialog after 5 seconds
          setTimeout(() => {
            setUpdateSuccessDialogOpen(false);
          }, 5000);
        }
      } catch (error: any) {
        console.error("Update member error:", error);
        console.error("GraphQL errors:", error?.graphQLErrors);
        console.error("Network error:", error?.networkError);
        console.log("Formik errors:", formik.errors);

        const errorMessage = error?.message || "";
        // Check if it's an email already in use error
        if (
          errorMessage.toLowerCase().includes("email") &&
          (errorMessage.toLowerCase().includes("already") ||
            errorMessage.toLowerCase().includes("exists") ||
            errorMessage.toLowerCase().includes("in use") ||
            errorMessage.toLowerCase().includes("duplicate"))
        ) {
          // Extract alternative email from error message if available
          const plusEmailMatch = errorMessage.match(/[\w.+-]+\+[\w.+-]+@[\w.-]+/);
          setAlternativeEmail(plusEmailMatch ? plusEmailMatch[0] : "");
          setEmailInUseDialogOpen(true);
        } else {
          setInvalidError(true);
          setCustomError(errorMessage || enumStrings.toastMessages.failedToUpdateMember);
        }
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  // Auto-fill form when member data is loaded
  useEffect(() => {
    if (memberData?.getChamberMember) {
      const member = memberData.getChamberMember;
      formik.setValues({
        id: member.id || "",
        chamberId: member.chamberId || "",
        categoryId: member.categoryId || [],
        name: member.name || "",
        description: member.description || "",
        website: member.website || "",
        facebook: member.facebook || "",
        instagram: member.instagram || "",
        threads: member.threads || "",
        linkedin: member.linkedin || "",
        twitter: member.twitter || "",
        tiktok: member.tiktok || "",
        youtube: member.youtube || "",
        googleReviewLink: member.googleReviewLink || "",
        address: {
          city: member.address?.city || "",
          postalCode: member.address?.postalCode || "",
          province: member.address?.province || "",
          streetAddress: member.address?.streetAddress || "",
        },
        phoneNumber: member.phoneNumber || "",
        phoneNumberExtension: member.phoneNumberExtension || "",
        contactName: member.contactName || "",
        email: member.email || "",
        logo: member.logo || "",
        showInApp: member.showInApp ?? true,
        hideAddressFromDirectory: member.hideAddressFromDirectory ?? false,
        adminConsoleLoginEmail: adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || "",
        primaryContactName: member.primaryContactName || "",
        primaryContactTitle: member.primaryContactTitle || "",
        primaryContactEmail: member.primaryContactEmail || "",
        primaryContactPhoneNumber: member.primaryContactPhoneNumber || "",
        primaryContactPhoneNumberExtension: member.primaryContactPhoneNumberExtension || "",
        adminConsoleContactName: member.adminConsoleContactName || "",
        cpaAdminCompanyAdminContactTitle: member.cpaAdminCompanyAdminContactTitle || "",
        cpaAdminCompanyAdminContactNumber: member.cpaAdminCompanyAdminContactNumber || "",
        cpaAdminCompanyAdminContactNumberExtension: member.cpaAdminCompanyAdminContactNumberExtension || "",
        cpaEmployeeAdoptionContactName: member.cpaEmployeeAdoptionContactName || "",
        cpaEmployeeAdoptionContactEmail: member.cpaEmployeeAdoptionContactEmail || "",
        cpaEmployeeAdoptionContactTitle: member.cpaEmployeeAdoptionContactTitle || "",
        cpaEmployeeAdoptionContactNumber: member.cpaEmployeeAdoptionContactNumber || "",
        cpaEmployeeAdoptionContactNumberExtension: member.cpaEmployeeAdoptionContactNumberExtension || "",
        customContacts: member.customContacts || getInitialStateOfCustomContacts(null),
        isSuspended: member.isSuspended || false,
      });
      savedValuesRef.current = JSON.stringify({
        id: member.id || "",
        chamberId: member.chamberId || "",
        categoryId: member.categoryId || [],
        name: member.name || "",
        description: member.description || "",
        website: member.website || "",
        facebook: member.facebook || "",
        instagram: member.instagram || "",
        threads: member.threads || "",
        linkedin: member.linkedin || "",
        twitter: member.twitter || "",
        tiktok: member.tiktok || "",
        youtube: member.youtube || "",
        googleReviewLink: member.googleReviewLink || "",
        address: { city: member.address?.city || "", postalCode: member.address?.postalCode || "", province: member.address?.province || "", streetAddress: member.address?.streetAddress || "" },
        phoneNumber: member.phoneNumber || "",
        phoneNumberExtension: member.phoneNumberExtension || "",
        contactName: member.contactName || "",
        email: member.email || "",
        logo: member.logo || "",
        showInApp: member.showInApp ?? true,
        hideAddressFromDirectory: member.hideAddressFromDirectory ?? false,
        adminConsoleLoginEmail: adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || "",
        primaryContactName: member.primaryContactName || "",
        primaryContactTitle: member.primaryContactTitle || "",
        primaryContactEmail: member.primaryContactEmail || "",
        primaryContactPhoneNumber: member.primaryContactPhoneNumber || "",
        primaryContactPhoneNumberExtension: member.primaryContactPhoneNumberExtension || "",
        adminConsoleContactName: member.adminConsoleContactName || "",
        cpaAdminCompanyAdminContactTitle: member.cpaAdminCompanyAdminContactTitle || "",
        cpaAdminCompanyAdminContactNumber: member.cpaAdminCompanyAdminContactNumber || "",
        cpaAdminCompanyAdminContactNumberExtension: member.cpaAdminCompanyAdminContactNumberExtension || "",
        cpaEmployeeAdoptionContactName: member.cpaEmployeeAdoptionContactName || "",
        cpaEmployeeAdoptionContactEmail: member.cpaEmployeeAdoptionContactEmail || "",
        cpaEmployeeAdoptionContactTitle: member.cpaEmployeeAdoptionContactTitle || "",
        cpaEmployeeAdoptionContactNumber: member.cpaEmployeeAdoptionContactNumber || "",
        cpaEmployeeAdoptionContactNumberExtension: member.cpaEmployeeAdoptionContactNumberExtension || "",
        customContacts: member.customContacts || getInitialStateOfCustomContacts(null),
        isSuspended: member.isSuspended || false,
      });

      // Detect which primary/admin field pairs initially match (linked state)
      const adminEmail = adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || "";
      const pName = member.primaryContactName || "";
      const aName = member.adminConsoleContactName || "";
      const pTitle = member.primaryContactTitle || "";
      const aTitle = member.cpaAdminCompanyAdminContactTitle || "";
      const pEmail = member.primaryContactEmail || "";
      const pPhone = member.primaryContactPhoneNumber || "";
      const aPhone = member.cpaAdminCompanyAdminContactNumber || "";
      const pPhoneExt = member.primaryContactPhoneNumberExtension || "";
      const aPhoneExt = member.cpaAdminCompanyAdminContactNumberExtension || "";

      fieldsInitiallyLinked.current = {
        adminConsoleContactName: pName === aName && pName !== "",
        cpaAdminCompanyAdminContactTitle: pTitle === aTitle && pTitle !== "",
        adminConsoleLoginEmail: pEmail === adminEmail && pEmail !== "",
        cpaAdminCompanyAdminContactNumber: pPhone === aPhone && pPhone !== "",
        cpaAdminCompanyAdminContactNumberExtension: pPhoneExt === aPhoneExt && pPhoneExt !== "",
      };

      // Reset touched state when member data reloads
      adminFieldsTouched.current = {
        adminConsoleContactName: false,
        cpaAdminCompanyAdminContactTitle: false,
        adminConsoleLoginEmail: false,
        cpaAdminCompanyAdminContactNumber: false,
        cpaAdminCompanyAdminContactNumberExtension: false,
      };

      // Check if member has featured banners activated
      const activationInfo = (member as any).activeMemberProfileAndPerksBannersSettings?.activationInfo;
      setIsFeaturedMember(!!activationInfo?.startDateUtcMilli);

      // Load banners if they exist
      if ((member as any).banner && Array.isArray((member as any).banner)) {
        const loadedBanners = (member as any).banner;
        const newBannersData = [
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
        ];

        loadedBanners.forEach((banner: any, idx: number) => {
          if (idx < 4) {
            if (banner.contentType === 'IMAGE' && banner.contentUrl) {
              newBannersData[idx] = {
                image: banner.contentUrl,
                videoUrl: null,
                redirectUrl: banner.imageLink || null,
              };
            } else if (banner.contentType === 'VIDEO' && banner.contentUrl) {
              newBannersData[idx] = {
                image: null,
                videoUrl: banner.contentUrl,
                redirectUrl: banner.imageLink || null,
              };
            }
          }
        });
        setBannersData(newBannersData);
      }

      if (member.categoryId && member.categoryId.length >= 2) {
        setCategoryDisable(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberData, adminConsoleLoginEmailData]);

  // Scroll to admin contact section when indicated
  useEffect(() => {
    if (scrollToSection === "adminContact" && memberData?.getChamberMember && adminContactRef.current) {
      setTimeout(() => {
        adminContactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [scrollToSection, memberData]);

  // Scroll to first validation error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      setTimeout(() => {
        // Try MUI error classes first
        let target = document.querySelector('.Mui-error, .MuiFormHelperText-root.Mui-error, [data-error="true"]');
        if (target) {
          const container = target.closest('.MuiFormControl-root') || target;
          container.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        // Fallback: find the first errored field by name
        const firstErrorKey = Object.keys(formik.errors)[0];
        if (firstErrorKey) {
          const field = document.querySelector(`[name="${firstErrorKey}"]`);
          if (field) {
            field.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }, 200);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors]);

  // Auto-sync primary contact → admin contact fields
  // Only syncs when fields were initially linked AND user hasn't focused the admin field
  useEffect(() => {
    if (fieldsInitiallyLinked.current.adminConsoleContactName && !adminFieldsTouched.current.adminConsoleContactName) {
      formik.setFieldValue("adminConsoleContactName", formik.values.primaryContactName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.primaryContactName]);

  useEffect(() => {
    if (fieldsInitiallyLinked.current.cpaAdminCompanyAdminContactTitle && !adminFieldsTouched.current.cpaAdminCompanyAdminContactTitle) {
      formik.setFieldValue("cpaAdminCompanyAdminContactTitle", formik.values.primaryContactTitle);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.primaryContactTitle]);

  useEffect(() => {
    if (fieldsInitiallyLinked.current.cpaAdminCompanyAdminContactNumber && !adminFieldsTouched.current.cpaAdminCompanyAdminContactNumber) {
      formik.setFieldValue("cpaAdminCompanyAdminContactNumber", formik.values.primaryContactPhoneNumber);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.primaryContactPhoneNumber]);

  useEffect(() => {
    if (fieldsInitiallyLinked.current.cpaAdminCompanyAdminContactNumberExtension && !adminFieldsTouched.current.cpaAdminCompanyAdminContactNumberExtension) {
      formik.setFieldValue("cpaAdminCompanyAdminContactNumberExtension", formik.values.primaryContactPhoneNumberExtension);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.primaryContactPhoneNumberExtension]);

  const handleCategoriesChange = (event: { target: { value: any } }) => {
    const value = event.target.value;
    formik.setFieldValue("categoryId", value);
    if (value.length >= 2) {
      setCategoryDisable(true);
    } else {
      setCategoryDisable(false);
    }
  };

  const handleDelete = (value: string) => {
    const newArray = _without(formik.values.categoryId, value);
    formik.setFieldValue("categoryId", newArray);
    if (newArray.length < 2) {
      setCategoryDisable(false);
    }
  };

  const handleCreateBase64 = useCallback(
    async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        formik.setFieldValue("logo", fileReader.result);
      };
    },
    [formik]
  );

  const deleteLogo = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setDeleteLogoConfirmationOpen(true);
  };

  const confirmDeleteLogo = () => {
    formik.setFieldValue("logo", undefined);
    setDeleteLogoConfirmationOpen(false);
  };

  const cancelDeleteLogo = () => {
    setDeleteLogoConfirmationOpen(false);
  };

  const handleDeleteMember = async () => {
    try {
      await deleteChamberMember({
        variables: { id: chamberMemberId || "" },
      });
      setDialogState({ open: true, message: enumStrings.toastMessages.memberDeletedSuccess, variant: "success" });
      setDeleteDialogOpen(false);
      redirectToManageChamber();
    } catch (error) {
      setDialogState({ open: true, message: enumStrings.toastMessages.failedToDeleteMember, variant: "error" });
      setDeleteDialogOpen(false);
    }
  };

  const confirmResetPassword = () => {
    setResetPasswordDialogOpen(false);
    setResetPasswordSuccessDialogOpen(true);
  };

  const handleResetPasswordSendEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await sendLoginInfoEmail({
        variables: {
          adminConsoleLoginEmail: adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || "",
          chamberMemberId: chamberMemberId || "",
          type: LoginInfoEmailTypes.ResetPassword,
        },
        refetchQueries: ["getAllChamberMembers"],
        awaitRefetchQueries: true,
      });
      if (data?.sendLoginInfoEmailToChamberMember?.loginInfoEmailSentSuccessfully) {
        setEmailSentSuccess(true);
      }
    } catch (error) {
      console.error("Send email error:", error);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleResetPasswordDownloadCredentials = () => {
    const csvDownloadButton = document.getElementById("reset-password-csv-download");
    if (csvDownloadButton) {
      csvDownloadButton.click();
    }
  };

  const handleCloseResetPasswordSuccess = () => {
    setResetPasswordSuccessDialogOpen(false);
    setNewPassword("");
    setEmailSentSuccess(false);
  };

  const confirmDelete = async () => {
    try {
      setLoader(true);
      await deleteChamberMember({
        variables: { id: chamberMemberId || "" },
      });
      setDialogState({ open: true, message: enumStrings.toastMessages.memberDeletedSuccess, variant: "success" });
      setDeleteDialogOpen(false);
      setLoader(false);
      redirectToManageChamber();
    } catch (error) {
      console.error("Delete member error:", error);
      setDialogState({ open: true, message: enumStrings.toastMessages.failedToDeleteMember, variant: "error" });
      setDeleteDialogOpen(false);
      setLoader(false);
    }
  };

  if (memberLoading) {
    return (
      <div className={classes.root} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress style={{ color: theme.palette.primary.main }} />
      </div>
    );
  }

  if (!chamberMemberId) {
    return (
      <div className={classes.root} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "18px", color: "#666" }}>{enumStrings.createMemberForm.noMemberSelected}</p>
          <Button
            style={{ backgroundColor: theme.palette.primary.main, color: "white", marginTop: "20px" }}
            onClick={redirectToManageChamber}
          >
            {enumStrings.createMemberForm.backToMembersList}
          </Button>
        </div>
      </div>
    );
  }

  console.log("EditChamberMember - chamberMemberId:", chamberMemberId);
  console.log("EditChamberMember - memberData:", memberData);
  console.log("EditChamberMember - memberLoading:", memberLoading);

  return (
    <div className={classes.root}>
      <UnsavedChangesPrompt isDirty={savedValuesRef.current !== "" && JSON.stringify(formik.values) !== savedValuesRef.current} />
      <div className={classes.titleContent}>
        <div className={classes.titleRow}>
          <div
            className={classes.title}
            style={{
              flexDirection: "column",
              marginBottom: "0",
            }}
          >
            <span>{enumStrings.editMemberForm.updateMemberTitle}</span>
            <span
              style={{
                marginLeft: "1px",
                fontWeight: "normal",
                fontSize: isMobile ? "16px" : "18px",
                lineHeight: isMobile ? "1.55" : undefined,
              }}
            >
              {enumStrings.editMemberForm.updateMemberDesc}
            </span>
            <div style={{ height: 6 }} />
            <div
              className={classes.pageHeaderButtonRow}
              style={{ display: "flex", gap: isMobile ? "8px" : "12px", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}
            >
              <VideoTutorialButton
                tutorialUrl="https://share.synthesia.io/embeds/videos/e9ae72e1-71f3-44fb-a5bc-d71d35cab428"
              />
              <ExportButton
                customOnClick={() => openInstructionPdf("Update Existing Member.pdf")}
                icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
                label={enumStrings.manageChamberEngage.viewInstructions}
                disablePadding
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.content}>
        <div>
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            {/* Action Buttons */}
            <div className={classes.actionButtonsContainer}>
              <Button
                className={classes.resetPasswordButton}
                onClick={() => setResetPasswordDialogOpen(true)}
                disabled={loader}
              >
                <img src={ResetPasswordButtonIcon} alt="Reset Password" className={classes.lockIcon} />
                {enumStrings.manageMembersPage.resetPassword}
              </Button>
              <Button
                className={classes.deleteButton}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={loader}
              >
                <img src={TrashIcon} alt="Delete" className={classes.deleteIcon} />
                {enumStrings.createMemberForm.deleteMember}
              </Button>
            </div>
            {/* Company Information Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyInformation}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.companyInfoDesc}
              </p>
              <div className={classes.companyInfoGrid}>
                {/* Company Name - Left Row 1 */}
                <div style={{ gridArea: "companyName" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyName}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.abcPlumbing}
                    className={classes.companyInput}
                    variant="outlined"
                    id="name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Company Description - Right Row 1+2 (spans two rows) */}
                <div style={{ gridArea: "description" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyDescription}</span>
                  </div>
                  <div className={classes.descriptionHelperText} dangerouslySetInnerHTML={{ __html: "Enter a company description to showcase your products and services, or click <b>Generate</b> to have <b>IDA (AI)</b> create one for you automatically." }} />

                  <div style={{
                    border: (formik.touched.description || formik.submitCount > 0) && formik.errors.description
                      ? "1px solid #f44336"
                      : idaGeneratedDescription
                        ? `2px solid ${theme.palette.success.main}`
                        : `1px solid ${theme.palette.text.primary}`,
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                  }}>
                    {idaGeneratedDescription && (
                      <div style={{
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 700,
                        fontSize: "10px",
                        lineHeight: "15px",
                        color: theme.palette.success.main,
                        marginBottom: "4px",
                      }}>
                        {enumStrings.idaGenerate.generatedLabel}
                      </div>
                    )}
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterCompanyDescription}
                      className={classes.companyInput}
                      variant="standard"
                      multiline
                      rows={3}
                      inputProps={{
                        maxLength: CHARACTER_LIMIT_DESCRIPTION,
                        autoComplete: "new-password",
                      }}
                      id="description"
                      name="description"
                      value={formik.values.description}
                      onChange={(e) => {
                        formik.handleChange(e);
                        if (idaGeneratedDescription) {
                          setIdaGeneratedDescription(false);
                        }
                      }}
                      error={
                        (formik.touched.description || formik.submitCount > 0) &&
                        Boolean(formik.errors.description)
                      }
                      helperText={
                        (formik.touched.description || formik.submitCount > 0) &&
                        formik.errors.description
                      }
                      InputProps={{
                        disableUnderline: true,
                      }}
                      InputLabelProps={{ shrink: false }}
                      style={{ flex: 1 }}
                      fullWidth
                    />
                  </div>
                  {/* IDA AI Helper Section - below the textarea: counter left, icon + generate right */}
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "12px", marginTop: "12px", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "14px", color: theme.palette.text.secondary, textAlign: "left", fontFamily: "Inter, sans-serif" }}>
                      {`${(formik.values.description || "").length}/${CHARACTER_LIMIT_DESCRIPTION} ${enumStrings.createMemberForm.characters}`}
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px" }}>
                      <img src={IdaChatbotIcon} alt="IDA" style={{ width: "40px", height: "40px", objectFit: "contain" }} />
                      {chamberInfo?.isSandbox ? (
                        <Button
                          variant="contained"
                          onClick={() => {
                            setIdaDialogOpen(true);
                          }}
                          style={{
                            backgroundColor: "#4CAF50",
                            color: "#FFFFFF",
                            fontSize: "14px",
                            fontWeight: 700,
                            borderRadius: "8px",
                            padding: "8px 24px",
                            textTransform: "uppercase",
                            minHeight: "36px",
                            boxShadow: "none",
                          }}
                        >
                          {idaGeneratedDescription ? enumStrings.idaGenerate.idaRegenerate : enumStrings.idaGenerate.generateButton}
                        </Button>
                      ) : (
                        <span style={{
                          backgroundColor: "#E53935",
                          color: "#FFFFFF",
                          border: "1px solid #E53935",
                          borderRadius: "10px",
                          padding: "6px 14px",
                          fontSize: "12px",
                          fontWeight: 700,
                          whiteSpace: "nowrap" as const,
                          display: "inline-block",
                        }}>
                          COMING SOON
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Company Website - Left Row 2 */}
                <div style={{ gridArea: "companyWebsite" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyWebsite}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyWebsite}
                    className={classes.companyInput}
                    variant="outlined"
                    id="website"
                    name="website"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                    error={formik.touched.website && Boolean(formik.errors.website)}
                    helperText={formik.touched.website && formik.errors.website}
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Main Company Contact Name - Left Row 3 */}
                <div style={{ gridArea: "contactName" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyContactName}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactName}
                    className={classes.companyInput}
                    variant="outlined"
                    id="contactName"
                    name="contactName"
                    value={formik.values.contactName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.contactName &&
                      Boolean(formik.errors.contactName)
                    }
                    helperText={
                      formik.touched.contactName && formik.errors.contactName
                    }
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Company Contact Phone - Right Row 3 */}
                <div style={{ gridArea: "phone" }}>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.companyContactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.phoneNumber &&
                        Boolean(formik.errors.phoneNumber)
                      }
                      helperText={
                        formik.touched.phoneNumber && formik.errors.phoneNumber
                      }
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="phoneNumberExtension"
                      name="phoneNumberExtension"
                      value={(formik.values as any).phoneNumberExtension}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>

                {/* Main Company Contact Email - Left Row 4 */}
                <div style={{ gridArea: "contactEmail" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.companyContactEmail}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Google Review URL - Right Row 4 */}
                <div style={{ gridArea: "googleReview" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.googleReviewUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterGoogleReviewUrl}
                    className={classes.companyInput}
                    variant="outlined"
                    id="googleReviewLink"
                    name="googleReviewLink"
                    value={formik.values.googleReviewLink as any}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </div>

                {/* Company Logo - Left Row 5 */}
                <div style={{ gridArea: "logo" }}>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.logo}</span>
                  </div>
                  <div className={classes.logoHelperText}>
                    {enumStrings.createMemberForm.logoHelperText}
                  </div>
                  <LogoPicker
                    logo={formik.values.logo}
                    deleteLogo={deleteLogo}
                    base64={(e: any) => handleCreateBase64(e)}
                  />
                </div>

                {/* Chamber Perks App Categories - Right Row 5 */}
                <div style={{ gridArea: "categories" }}>
                  <div className={classes.fieldLabel}>
                    <span style={{ display: "flex", alignItems: "flex-start", gap: "4px" }}>
                      {enumStrings.createMemberForm.chamberPerksCategories} <span className={classes.requiredAsterisk}>*</span>
                      <img src={IdaChatbotIcon} alt="IDA" style={{ width: "73px", height: "57px", objectFit: "contain", marginTop: "-15px" }} />
                    </span>
                  </div>
                  <div className={classes.categoryHelperText}>
                    Our AI, "<span style={{ fontWeight: 700 }}>IDA</span>" has selected the two most relevant categories for you based on your company description. You're welcome to adjust these by removing or selecting different categories from the dropdown.
                  </div>
                  <Categories
                    selectedCategories={formik.values.categoryId}
                    handleCategoriesChange={handleCategoriesChange}
                    disableCategories={categoryDisable}
                    handleDelete={handleDelete}
                    error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                  />
                  {formik.touched.categoryId && formik.errors.categoryId && (
                  <FormHelperText
                    component="legend"
                    style={{
                      color: "#f44336",
                      paddingLeft: "0",
                      paddingTop: "5px",
                      textAlign: "left",
                    }}
                  >
                    {formik.errors.categoryId}
                  </FormHelperText>
                  )}
                  <p style={{ fontSize: "16px", color: "#24272C", marginTop: "16px", marginBottom: "0" }} dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.categoryDescriptionHelper }} />
                </div>
              </div>
            </div>

            {/* Company Address Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyAddress}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.companyAddressDesc}
              </p>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={(formik.values as any).hideAddressFromDirectory}
                    onChange={formik.handleChange}
                    name="hideAddressFromDirectory"
                    color="default"
                    style={{ color: "#000" }}
                  />
                }
                label={
                  <span style={{ fontSize: "14px", color: "#000" }}>
                    {enumStrings.createMemberForm?.hideAddressCheckbox}
                  </span>
                }
                style={{ marginBottom: "16px", marginLeft: "0px" }}
              />

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.streetAddress}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterStreetAddress}
                    className={classes.companyInput}
                    variant="outlined"
                    id="address.streetAddress"
                    name="address.streetAddress"
                    value={formik.values.address.streetAddress}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.city}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterCity}
                    className={classes.companyInput}
                    variant="outlined"
                    id="address.city"
                    name="address.city"
                    value={formik.values.address.city}
                    onChange={formik.handleChange}
                    error={
                      (formik.touched.address as any)?.city &&
                      Boolean((formik.errors.address as any)?.city)
                    }
                    helperText={
                      (formik.touched.address as any)?.city &&
                      (formik.errors.address as any)?.city
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.provinceState}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <div>
                    <ProvincePicker
                      formikVal={formik.values.address?.province}
                      formikErrors={
                        (formik.touched.address as any)?.province &&
                        Boolean((formik.errors.address as any)?.province)
                      }
                      formikStringError={(formik.errors.address as any)?.province}
                      formikSetFieldValue={formik.setFieldValue}
                    />
                  </div>
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.postalZipCode}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterPostalCode}
                    className={classes.companyInput}
                    variant="outlined"
                    id="address.postalCode"
                    name="address.postalCode"
                    value={formik.values.address.postalCode}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>
            </div>

            {/* Links to Socials Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.linksToSocials}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.companyInfoDesc}
              </p>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.facebookUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={`Enter ${enumStrings.createMemberForm.facebookUrl}`}
                    className={classes.sectionInput}
                    variant="outlined"
                    id="facebook"
                    name="facebook"
                    value={formik.values.facebook}
                    onChange={formik.handleChange}
                    error={formik.touched.facebook && Boolean(formik.errors.facebook)}
                    helperText={formik.touched.facebook && formik.errors.facebook}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.instagramUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={`Enter ${enumStrings.createMemberForm.instagramUrl}`}
                    className={classes.sectionInput}
                    variant="outlined"
                    id="instagram"
                    name="instagram"
                    value={formik.values.instagram}
                    onChange={formik.handleChange}
                    error={formik.touched.instagram && Boolean(formik.errors.instagram)}
                    helperText={formik.touched.instagram && formik.errors.instagram}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.threadsUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterThreadsUrl}
                    className={classes.sectionInput}
                    variant="outlined"
                    id="threads"
                    name="threads"
                    value={formik.values.threads}
                    onChange={formik.handleChange}
                    error={formik.touched.threads && Boolean(formik.errors.threads)}
                    helperText={formik.touched.threads && formik.errors.threads}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.linkedinUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterLinkedinUrl}
                    className={classes.sectionInput}
                    variant="outlined"
                    id="linkedin"
                    name="linkedin"
                    value={formik.values.linkedin}
                    onChange={formik.handleChange}
                    error={formik.touched.linkedin && Boolean(formik.errors.linkedin)}
                    helperText={formik.touched.linkedin && formik.errors.linkedin}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.xTwitterUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={`Enter ${enumStrings.createMemberForm.xTwitterUrl}`}
                    className={classes.sectionInput}
                    variant="outlined"
                    id="twitter"
                    name="twitter"
                    value={formik.values.twitter}
                    onChange={formik.handleChange}
                    error={formik.touched.twitter && Boolean(formik.errors.twitter)}
                    helperText={formik.touched.twitter && formik.errors.twitter}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.tiktokUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={`Enter ${enumStrings.createMemberForm.tiktokUrl}`}
                    className={classes.sectionInput}
                    variant="outlined"
                    id="tiktok"
                    name="tiktok"
                    value={formik.values.tiktok}
                    onChange={formik.handleChange}
                    error={formik.touched.tiktok && Boolean(formik.errors.tiktok)}
                    helperText={formik.touched.tiktok && formik.errors.tiktok}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.youtubeUrl}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={`Enter ${enumStrings.createMemberForm.youtubeUrl}`}
                    className={classes.sectionInput}
                    variant="outlined"
                    id="youtube"
                    name="youtube"
                    value={formik.values.youtube}
                    onChange={formik.handleChange}
                    error={formik.touched.youtube && Boolean(formik.errors.youtube)}
                    helperText={formik.touched.youtube && formik.errors.youtube}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div />
              </div>
            </div>

            {/* Company Primary Contact Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyPrimaryContact}</p>
              </div>
              <div style={{ marginBottom: "20px" }}>
                <p style={{ fontSize: "16px", lineHeight: "24px", color: "#000000", fontWeight: 400, marginBottom: "12px" }}
                  dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.primaryContactDesc1 }}
                />
                <p style={{ fontSize: "16px", lineHeight: "24px", color: "#000000", fontWeight: 400, marginBottom: "12px" }}
                  dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.primaryContactDesc2 }}
                />
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.primaryContactName}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactName}
                    className={classes.companyInput}
                    variant="outlined"
                    id="primaryContactName"
                    name="primaryContactName"
                    value={formik.values.primaryContactName}
                    onChange={formik.handleChange}
                    error={formik.touched.primaryContactName && Boolean(formik.errors.primaryContactName)}
                    helperText={formik.touched.primaryContactName && formik.errors.primaryContactName}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.title}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                    className={classes.companyInput}
                    variant="outlined"
                    id="primaryContactTitle"
                    name="primaryContactTitle"
                    value={formik.values.primaryContactTitle}
                    onChange={formik.handleChange}
                    error={formik.touched.primaryContactTitle && Boolean(formik.errors.primaryContactTitle)}
                    helperText={formik.touched.primaryContactTitle && formik.errors.primaryContactTitle}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow} style={{ alignItems: "end" }}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.primaryContactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <div style={{ fontSize: "16px", color: "#000000", marginBottom: "8px" }}>
                    {enumStrings.createMemberForm.emailUsernameNote}
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="primaryContactEmail"
                    name="primaryContactEmail"
                    value={formik.values.primaryContactEmail}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.primaryContactEmail &&
                      Boolean(formik.errors.primaryContactEmail)
                    }
                    helperText={
                      formik.touched.primaryContactEmail &&
                      formik.errors.primaryContactEmail
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.primaryContactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="primaryContactPhoneNumber"
                      name="primaryContactPhoneNumber"
                      value={(formik.values as any).primaryContactPhoneNumber}
                      onChange={formik.handleChange}
                      error={formik.touched.primaryContactPhoneNumber && Boolean(formik.errors.primaryContactPhoneNumber)}
                      helperText={formik.touched.primaryContactPhoneNumber && formik.errors.primaryContactPhoneNumber}
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="primaryContactPhoneNumberExtension"
                      name="primaryContactPhoneNumberExtension"
                      value={(formik.values as any).primaryContactPhoneNumberExtension}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Chamber Perks App Company Administrator Contact Section */}
            <div className={classes.formSection} ref={adminContactRef}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.chamberPerksAdminContact}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.adminContactAutoAssignDesc}
              </p>
              <p className={classes.sectionHelperText} style={{ fontWeight: 700 }}>
                {enumStrings.createMemberForm.adminContactNotAppear}
              </p>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.contactName}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactName}
                    className={classes.companyInput}
                    variant="outlined"
                    id="adminConsoleContactName"
                    name="adminConsoleContactName"
                    value={formik.values.adminConsoleContactName}
                    onFocus={() => { adminFieldsTouched.current.adminConsoleContactName = true; }}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.adminConsoleContactName &&
                      Boolean(formik.errors.adminConsoleContactName)
                    }
                    helperText={
                      formik.touched.adminConsoleContactName &&
                      formik.errors.adminConsoleContactName
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.title}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaAdminCompanyAdminContactTitle"
                    name="cpaAdminCompanyAdminContactTitle"
                    value={formik.values.cpaAdminCompanyAdminContactTitle}
                    onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactTitle = true; }}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow} style={{ alignItems: "end" }}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.contactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#666666", marginBottom: "8px" }}>
                    {enumStrings.createMemberForm.adminEmailNote}
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="adminConsoleLoginEmail"
                    name="adminConsoleLoginEmail"
                    value={formik.values.adminConsoleLoginEmail || ""}
                    onFocus={() => { adminFieldsTouched.current.adminConsoleLoginEmail = true; }}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.adminConsoleLoginEmail &&
                      Boolean(formik.errors.adminConsoleLoginEmail)
                    }
                    helperText={
                      formik.touched.adminConsoleLoginEmail &&
                      formik.errors.adminConsoleLoginEmail
                    }
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.contactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaAdminCompanyAdminContactNumber"
                      name="cpaAdminCompanyAdminContactNumber"
                      value={formik.values.cpaAdminCompanyAdminContactNumber || ""}
                      onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactNumber = true; }}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.cpaAdminCompanyAdminContactNumber &&
                        Boolean(formik.errors.cpaAdminCompanyAdminContactNumber)
                      }
                      helperText={
                        formik.touched.cpaAdminCompanyAdminContactNumber &&
                        formik.errors.cpaAdminCompanyAdminContactNumber
                      }
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaAdminCompanyAdminContactNumberExtension"
                      name="cpaAdminCompanyAdminContactNumberExtension"
                      value={(formik.values as any).cpaAdminCompanyAdminContactNumberExtension || ""}
                      onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactNumberExtension = true; }}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact for Employee Adoption Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.employeeAdoptionContact}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createMemberForm.employeeAdoptionDescEdit}
              </p>
              <p className={classes.sectionHelperText} style={{ fontWeight: 700 }}>
                {enumStrings.createMemberForm.employeeAdoptionNotAppear}
              </p>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.employeeContactName}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactName}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaEmployeeAdoptionContactName"
                    name="cpaEmployeeAdoptionContactName"
                    value={formik.values.cpaEmployeeAdoptionContactName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.cpaEmployeeAdoptionContactName &&
                      Boolean(formik.errors.cpaEmployeeAdoptionContactName)
                    }
                    helperText={
                      formik.touched.cpaEmployeeAdoptionContactName &&
                      formik.errors.cpaEmployeeAdoptionContactName
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.title}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaEmployeeAdoptionContactTitle"
                    name="cpaEmployeeAdoptionContactTitle"
                    value={formik.values.cpaEmployeeAdoptionContactTitle}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div>
                  <div className={classes.fieldLabel}>
                    <span>{enumStrings.createMemberForm.employeeContactEmail}</span>
                  </div>
                  <TextField
                    type="text"
                    placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactEmail}
                    className={classes.companyInput}
                    variant="outlined"
                    id="cpaEmployeeAdoptionContactEmail"
                    name="cpaEmployeeAdoptionContactEmail"
                    value={formik.values.cpaEmployeeAdoptionContactEmail}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.cpaEmployeeAdoptionContactEmail &&
                      Boolean(formik.errors.cpaEmployeeAdoptionContactEmail)
                    }
                    helperText={
                      formik.touched.cpaEmployeeAdoptionContactEmail &&
                      formik.errors.cpaEmployeeAdoptionContactEmail
                    }
                    InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                  />
                </div>
                <div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                      <span>{enumStrings.createMemberForm.employeeContactPhone}</span>
                    </div>
                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                      <span>{enumStrings.createMemberForm?.extensionNo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactPhone}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaEmployeeAdoptionContactNumber"
                      name="cpaEmployeeAdoptionContactNumber"
                      value={formik.values.cpaEmployeeAdoptionContactNumber}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.cpaEmployeeAdoptionContactNumber &&
                        Boolean(formik.errors.cpaEmployeeAdoptionContactNumber)
                      }
                      helperText={
                        formik.touched.cpaEmployeeAdoptionContactNumber &&
                        formik.errors.cpaEmployeeAdoptionContactNumber
                      }
                      InputLabelProps={{ shrink: false }}
                      InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ flex: 1 }}
                    />
                    <TextField
                      type="text"
                      placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                      className={classes.companyInput}
                      variant="outlined"
                      id="cpaEmployeeAdoptionContactNumberExtension"
                      name="cpaEmployeeAdoptionContactNumberExtension"
                      value={(formik.values as any).cpaEmployeeAdoptionContactNumberExtension || ""}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ autoComplete: "new-password" }}
                      style={{ width: "100px" }}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Member Profile Banners Section */}
            <div className={classes.formSection}>
              <div className={isFeaturedMember ? classes.sectionHeaderGreen : classes.sectionHeaderBlack}>
                <p className={classes.sectionTitle}>
                  {isFeaturedMember
                    ? enumStrings.editMemberForm.featuredMemberActivatedTitle
                    : enumStrings.editMemberForm.featuredMemberNotActivatedTitle}
                </p>
              </div>

              {isFeaturedMember ? (
                <p className={classes.bannerInfoText}
                  dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.featuredMemberUpgradedDesc }}
                />
              ) : (
                <>
                  <p className={classes.bannerInfoText} style={{ color: "#666666", textAlign: "left" }}>
                    {enumStrings.createMemberForm.featuredMemberBannersDesc}
                  </p>
                  <p className={classes.bannerInfoTextLarge} style={{ color: "#24272C" }}>
                    {enumStrings.editMemberForm.featuredMemberNotActivatedDesc}
                  </p>
                </>
              )}

              <FeaturedMemberBannerGuidance className={classes.bannerRecommendationText} />

              <div className={classes.bannersGrid}>
                {isFeaturedMember ? (
                  bannersData.map((banner, index) => (
                    <div key={index} className={classes.bannerContainer}>
                      <div className={classes.bannerLabel}>
                        {enumStrings.editMemberForm.bannerLabel} {index + 1}
                      </div>
                      {banner.image || banner.videoUrl ? (
                        <div className={classes.bannerCard}>
                          <div className={classes.bannerImageContainer}>
                            {banner.image ? (
                              <img
                                src={banner.image}
                                alt={`Banner ${index + 1}`}
                                className={classes.bannerImage}
                              />
                            ) : (
                              <div className={classes.videoPlaceholder}>
                                <img src={PlayVideoIcon} alt="Play" className={classes.iconSize36} />
                                <span className={classes.videoWatchText}>{enumStrings.editMemberForm.watchVideo}</span>
                              </div>
                            )}
                          </div>
                          <div className={classes.bannerActions}>
                            <div className={classes.editDeleteButtons}>
                              <IconButton
                                size="small"
                                className={classes.editButton}
                                onClick={() => handleOpenBannerModal(index + 1)}
                              >
                                <img src={EditOrangeIcon} alt="Edit" className={classes.iconSize20} />
                              </IconButton>
                              <IconButton
                                size="small"
                                className={classes.trashButton}
                                onClick={() => handleDeleteBanner(index)}
                              >
                                <img src={TrashIcon} alt="Delete" className={`${classes.iconSize20} ${classes.iconInverted}`} style={{ height: "22px" }} />
                              </IconButton>
                            </div>
                            <div className={classes.arrowButtons}>
                              <IconButton
                                size="small"
                                className={classes.arrowButton}
                                onClick={() => handleMoveBanner(index, 'left')}
                                disabled={index === 0}
                              >
                                <ArrowBackIosIcon className={classes.arrowIconLeft} />
                              </IconButton>
                              <IconButton
                                size="small"
                                className={classes.arrowButton}
                                onClick={() => handleMoveBanner(index, 'right')}
                                disabled={index === bannersData.length - 1}
                              >
                                <ArrowForwardIosIcon className={classes.arrowIconSmall} />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => handleOpenBannerModal(index + 1)}
                          className={classes.bannerCardEmpty}
                        >
                          <img
                            src={UploadRoundIcon}
                            alt="Upload"
                            className={classes.iconSize36}
                          />
                          <p className={classes.uploadBannerText}>
                            {enumStrings.editMemberForm.uploadBanner}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  [1, 2, 3, 4].map((num) => (
                    <div key={num}>
                      <p className={classes.bannerLabel} style={{ color: "#888888", marginBottom: "16px" }}>
                        {enumStrings.editMemberForm.bannerLabel} {num}
                      </p>
                      <div className={classes.bannerSlotDisabled}>
                        <img src={UploadRoundIcon} alt="Upload" className={classes.bannerSlotDisabledIcon} />
                        <span className={classes.bannerSlotDisabledText}>{enumStrings.editMemberForm.uploadBanner}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <p className={classes.bannerRecommendationText} style={{ marginTop: "24px", marginBottom: "24px" }}
                dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.bannerPreviewFooter }}
              />

              {/* Preview Banners Button */}
              <div className={classes.centerFlexContainer}>
                <Button
                  className={classes.previewBannersButton}
                  onClick={handlePreviewBanners}
                  disabled={!isFeaturedMember}
                  style={!isFeaturedMember ? { cursor: "not-allowed" } : {}}
                >
                  {enumStrings.createMemberForm.previewBannersButton}
                </Button>
              </div>
            </div>

            {/* Membership Status Section */}
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>{enumStrings.chamberMember.memberStatus}</p>
              </div>
              <p className={classes.sectionHelperText}>
                {enumStrings.createChamberMemberText.tooltip.membershipStatus.title}
              </p>
              <ChamberMemberIsSuspendedRadioGroup
                formikSetFieldValue={formik.setFieldValue}
                formikErrors={formik.errors}
                formikValues={formik.values}
                hideHeader
                hideTooltips={true}
              />
            </div>

            <div
              style={{
                marginTop: "35px",
              }}
            >
              {invalidError ? (
                <div className={classes.invalidError}>{customError}</div>
              ) : null}
            </div>
            {Object.keys(formik?.errors || {}).length > 0 ? (
              <div>
                <div className={classes.invalidError}>
                  {enumStrings.ChamberMemberSelector.pleaseAddressAnyIssues}
                </div>
                <br />
              </div>
            ) : null}

            {/* Update Button */}
            <div style={{ textAlign: "center", paddingBottom: "40px" }}>
              <Button
                type="submit"
                className={classes.updateButton}
                disabled={loader || buttonDisabled}
              >
                {loader ? (
                  <CircularProgress
                    style={{
                      color: "white",
                    }}
                    size={24}
                  />
                ) : (
                  enumStrings.editMemberForm.updateMemberButton
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Banner Upload Modal */}
      <BannerUploadModal
        open={bannerModalOpen}
        onClose={() => setBannerModalOpen(false)}
        bannerIndex={currentBannerIndex}
        useFeaturedMemberGuidance
        initialData={bannersData[currentBannerIndex - 1] || {}}
        onSave={handleSaveBanner}
      />

      {/* Reset Password Dialog */}
      <ResetDashboardPasswordDialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        onSendEmail={() => {
          setResetPasswordDialogOpen(false);
          setResetPasswordSuccessDialogOpen(true);
        }}
        onPreviewEmail={() => {
          setResetPasswordDialogOpen(false);
          setResetPasswordPreviewEmailOpen(true);
        }}
        onEdit={() => {
          setResetPasswordDialogOpen(false);
          if (adminContactRef.current) {
            adminContactRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
        loading={loader}
        contactName={memberData?.getChamberMember?.adminConsoleContactName || memberData?.getChamberMember?.contactName || formik.values.adminConsoleContactName || formik.values.contactName || ""}
        contactEmail={adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || formik.values.adminConsoleLoginEmail || formik.values.email || ""}
      />

      {/* Reset Password Preview Email */}
      <SampleLoginInfoEmailModal
        open={resetPasswordPreviewEmailOpen}
        handleClose={() => setResetPasswordPreviewEmailOpen(false)}
        emailHtml={null}
        modalType="resetPassword"
        memberName={memberData?.getChamberMember?.name || ""}
        memberEmail={adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || ""}
        chamberName={chamberInfo?.name || ""}
      />

      {/* Delete Member Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        classes={{ paper: classes.dialogPaper }}
      >
        <IconButton
          className={classes.dialogCloseButton}
          onClick={() => setDeleteDialogOpen(false)}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent style={{ padding: "40px 40px 36px" }}>
          <div className={classes.dialogIconContainer}>
            <div className={`${classes.dialogIcon} ${classes.dialogIconDelete}`}>
              <img src={TrashIcon} alt="Delete" style={{ width: "40px", height: "40px", filter: "brightness(0) invert(1)" }} />
            </div>
          </div>
          <div className={classes.dialogTitle}>{enumStrings.dialogs.deleteMember}</div>
          <div className={classes.dialogDescription}>
            {enumStrings.dialogs.areYouSureDeleteMember}?
          </div>
          <div className={classes.dialogDescription} style={{ fontStyle: "italic", fontSize: "12px" }}>
            {enumStrings.dialogs.cannotUndo}
          </div>
          <div className={classes.dialogButtons}>
            <Button
              className={classes.dialogButtonDelete}
              onClick={confirmDelete}
              disabled={loader}
            >
              {loader ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                <>
                  <img src={TrashIcon} alt="" style={{ width: "20px", height: "20px", filter: "brightness(0) invert(1)", marginRight: "8px" }} />
                  {enumStrings.dialogs.delete}
                </>
              )}
            </Button>
            <Button
              className={classes.dialogButtonCancel}
              onClick={() => setDeleteDialogOpen(false)}
            >
              {enumStrings.dialogs.cancel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password - New Temporary Password Generated Dialog */}
      <SendLoginInformationModal
        open={resetPasswordSuccessDialogOpen}
        handleClose={handleCloseResetPasswordSuccess}
        memberCompanyName={memberData?.getChamberMember?.name || ""}
        memberAdminPortalEmail={adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || ""}
        chamberMemberId={chamberMemberId || ""}
        type="resetPassword"
        isSuspended={formik.values.isSuspended ? true : false}
      />

      {/* Send Login Credentials Dialog - after admin email change */}
      <SendLoginInformationModal
        open={sendCredentialsDialogOpen}
        handleClose={() => setSendCredentialsDialogOpen(false)}
        memberCompanyName={memberData?.getChamberMember?.name || ""}
        memberAdminPortalEmail={adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || ""}
        chamberMemberId={chamberMemberId || ""}
        type="changeLoginEmailUsername"
        isSuspended={formik.values.isSuspended ? true : false}
        credentialsSentAtUtcMilli={memberData?.getChamberMember?.credentialsSentAtUtcMilli}
      />

      {/* Update Success Dialog */}
      <SuccessDialog
        open={updateSuccessDialogOpen}
        onClose={() => setUpdateSuccessDialogOpen(false)}
        message={enumStrings.editMemberForm.memberUpdatedSuccess}
      />

      {/* Email Already In Use Dialog */}
      <Dialog
        open={emailInUseDialogOpen}
        onClose={() => setEmailInUseDialogOpen(false)}
        classes={{ paper: classes.emailInUseDialogPaper }}
      >
        <IconButton
          className={classes.emailInUseCloseButton}
          onClick={() => setEmailInUseDialogOpen(false)}
        >
          <CloseIcon style={{ fontSize: "20px", color: (theme.palette as any).delete.main }} />
        </IconButton>
        <DialogContent style={{ padding: "0 34px 30px 34px", textAlign: "center" }}>
          <div className={classes.emailInUseIconContainer}>
            <div className={classes.emailInUseIcon}>
              <ErrorOutlineIcon style={{ fontSize: "60px", color: "#FFFFFF" }} />
            </div>
          </div>
          <div className={classes.emailInUseTitle}>
            {enumStrings.editMemberForm.emailAlreadyInUse}
          </div>
          <div className={classes.emailInUseDescription}>
            {enumStrings.editMemberForm.emailAlreadyInUseDesc}
          </div>
          <div className={classes.emailInUseDescription}>
            <span dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.emailAlreadyInUseDesc2 }} />
            {alternativeEmail ? (
              <> (<b>{alternativeEmail}</b>). </>
            ) : ". "}
            {enumStrings.editMemberForm.emailAlreadyInUseDesc3}
            {alternativeEmail ? (
              <> <b>{alternativeEmail.replace(/\+[^@]+/, "")}</b>.</>
            ) : "."}
          </div>
          <div className={classes.emailInUseDescription}>
            <span dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.emailAlreadyInUseUseAlt }} />
          </div>
          <div className={classes.emailInUseButtons}>
            <Button
              className={classes.emailInUseButtonAlt}
              onClick={async () => {
                setEmailInUseDialogOpen(false);
                // Set the alternative email and retry submission
                if (alternativeEmail) {
                  await formik.setFieldValue("adminConsoleLoginEmail", alternativeEmail);
                  formik.submitForm();
                }
              }}
              variant="contained"
            >
              {enumStrings.editMemberForm.useAlternative}
            </Button>
            <Button
              className={classes.emailInUseButtonBack}
              onClick={() => {
                setEmailInUseDialogOpen(false);
              }}
              variant="contained"
            >
              {enumStrings.editMemberForm.back}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* IDA Generate Description Dialog */}
      <IDAGenerateModal
        open={idaDialogOpen}
        onClose={() => setIdaDialogOpen(false)}
        keyDetails={idaKeyDetails}
        onKeyDetailsChange={setIdaKeyDetails}
        entityName={formik.values.name || "the company"}
        entityType="company"
        onGenerate={(generated) => {
          setIdaGeneratedText(generated);
          setIdaDialogOpen(false);
          setOverwriteDialogOpen(true);
        }}
      />

      {/* IDA Overwrite Confirmation Dialog */}
      <IDAOverwriteDialog
        open={overwriteDialogOpen}
        onClose={() => setOverwriteDialogOpen(false)}
        onRegenerate={() => {
          setOverwriteDialogOpen(false);
          setIdaDialogOpen(true);
        }}
        onConfirm={() => {
          formik.setFieldValue("description", idaGeneratedText);
          setOverwriteDialogOpen(false);
          setIdaGeneratedDescription(true);
          setIdaSuccessDialogOpen(true);
          setTimeout(() => setIdaSuccessDialogOpen(false), 5000);
        }}
        title={enumStrings.idaGenerate.overwriteTitle}
        description={enumStrings.idaGenerate.overwriteDesc}
        generatedLabel={enumStrings.idaGenerate.generatedLabel}
        generatedText={idaGeneratedText}
      />

      {/* IDA Success Dialog */}
      <SuccessDialog
        open={idaSuccessDialogOpen}
        onClose={() => setIdaSuccessDialogOpen(false)}
        message={enumStrings.toastMessages.idaGeneratedSuccess}
      />

      {/* Banner Preview Modal */}
      <BannerPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        banners={previewBannersList}
      />

      <DeleteBannerModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setPendingDeleteIndex(-1);
        }}
        onDelete={confirmDeleteBanner}
      />

      {/* Delete Logo Confirmation Modal */}
      <DeleteLogoModal
        open={deleteLogoConfirmationOpen}
        onClose={cancelDeleteLogo}
        onDelete={confirmDeleteLogo}
        title={enumStrings.dialogs.deleteLogo}
        confirmText={enumStrings.dialogs.areYouSureDeleteLogo}
        description={enumStrings.dialogs.cannotUndo}
      />

      <SuccessDialog
        open={dialogState.open}
        onClose={() => setDialogState(prev => ({ ...prev, open: false }))}
        message={dialogState.message}
        variant={dialogState.variant}
        autoDismissMs={5000}
      />
    </div>
  );
};

export default EditChamberMember;
